/**
 *
 * Paytm
 *
 */

import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import {
  Button,
  Conditional,
  AlertModal,
  Text,
  Space,
} from '@cashfree-intl/coherent';

import { SUCCESS } from '../../../utils/constants';
import { getItemFromLocalStorage } from '../../../utils/common';

import {
  PAYTM,
  START,
  FIRST,
  SECOND,
  COMPLETED,
  CONNECT_PAYTM_ACCOUNT_SUCCESS,
  PAYTM_REPORT_GENERATED,
  // RESET_PAYTM_MODAL,
  PAYTM_MODAL,
  GMAIL_LINK,
  ERROR_CONFIG,
  PAYTM_CODE,
} from '../constants';

import CustomIcons from '../../../components/CustomIcons';
import PaytmModal from './PaytmModal';

function Paytm(props) {
  const {
    data,
    loading,
    getPaytmTestConfig,
    getPaymentMethods,
    getPaytmCheckSumHash,
    addPaytmProdConfig,
    addPaytmTestConfig,
    // openConfirmModal,
    showToast,
    checkPaymentMethodEnabled,
  } = props;

  const [modalName, resetModalName] = useState('');
  const [testLoader, resetTestLoader] = useState(false);
  const [showPaytmForm, resetShowPaytmForm] = useState(true);
  const [isEditMode, resetEditMode] = useState(false);

  const { paytmProdConfig, paytmTestConfig } = data;

  const accountStatus = checkPaymentMethodEnabled(PAYTM_CODE)
    ? 'ACTIVE'
    : 'Pending Action';

  const { stage } = paytmTestConfig;

  useEffect(() => {
    const handleMessageEvent = event => {
      if (/^react-devtools/gi.test(event.data.source)) return;

      if (event.origin === window.location.origin) {
        if (event.data.status === SUCCESS && stage === COMPLETED)
          getPaymentMethods();
        else if (event.data.status === SUCCESS && stage !== COMPLETED)
          getPaytmTestConfig();
        else if (event.data.status === ERROR_CONFIG && stage === COMPLETED) {
          resetShowPaytmForm(true);
          setTimeout(() => showToast({ message: event.data.msg }), 100);
        } else if (event.data.status === ERROR_CONFIG && stage !== COMPLETED) {
          resetEditMode(true);
          resetShowPaytmForm(true);
          setTimeout(() => showToast({ message: event.data.msg }), 100);
        } else setTimeout(() => showToast({ message: event.data.msg }), 100);

        resetTestLoader(false);
      }
    };

    window.addEventListener('message', handleMessageEvent, false);

    return () =>
      window.removeEventListener('message', handleMessageEvent, false);
  }, [paytmTestConfig, paytmProdConfig]);

  const closeModal = () => {
    resetTestLoader(false);
    resetModalName('');
  };

  const renderActiveCardContent = () => (
    <div className="third-party-payment-method active">
      <Space justifyContent="space-between" alignItems="center">
        <Space className="payment-method-card-header">
          <CustomIcons componentName={PAYTM} />
          <div className="status active">{accountStatus}</div>
        </Space>
        {/* Will add deactivate feature once backend is ready */}
        {/* <Button
          secondary
          size="small"
          className="reset-cta"
          onClick={() => openConfirmModal(RESET_PAYTM_MODAL)}
          loading={loading}
          disabled={loading}
        >
          Deactivate
        </Button> */}
      </Space>
      <Text
        className="payment-method-card-content mr-top-16"
        variant="b14"
        color="bodyLight"
      >
        Your Paytm Business account is active with Cashfree Payments.
      </Text>
    </div>
  );

  const getDescriptionByStage = () => {
    switch (stage) {
      case START:
      case FIRST:
      case SECOND:
        return 'Complete all the test transactions to generate a report in order to validate the linking of your Cashfree account with your PayTM wallet.';
      case COMPLETED:
        return 'We have generated a report on successful transactions, share it with your paytm AM and get the Prod Credentials.';
      default:
        return 'Connect your Paytm business account with Cashfree to enable this payment method.';
    }
  };

  const connectAccount = () => {
    if (
      stage !== COMPLETED ||
      (stage === COMPLETED && !isEmpty(paytmProdConfig))
    )
      resetShowPaytmForm(false);
    else resetShowPaytmForm(true);
    resetModalName(PAYTM_MODAL);
  };

  const renderStage = () => (
    <Conditional if={!isEmpty(paytmTestConfig)}>
      <Space className="mr-top-16" alignItems="center">
        <Space alignItems="center" className="stage success">
          <CustomIcons componentName="GREEN_TICK_SMALL" />
          <Text variant="b12" color="bodyLight">
            Test API Credentials
          </Text>
        </Space>
        <div className="stage-line">
          <CustomIcons
            componentName={stage === COMPLETED ? 'LINE' : 'DOTTED_LINE'}
          />
        </div>
        <Space
          alignItems="center"
          className={`stage ${stage === COMPLETED ? 'success' : 'disabled'}`}
        >
          <CustomIcons
            componentName="GREEN_TICK_SMALL"
            fill={stage !== COMPLETED ? 'var(--SECONDARY_LIGHTER)' : ''}
          />
          <Text
            variant="b12"
            color={stage === COMPLETED ? 'bodyLight' : 'placeholder'}
          >
            Test Transactions
          </Text>
        </Space>
        <div className="stage-line">
          <CustomIcons componentName="DOTTED_LINE" />
        </div>
        <Space alignItems="center" className="stage disabled">
          <CustomIcons
            componentName="GREEN_TICK_SMALL"
            fill="var(--SECONDARY_LIGHTER)"
          />
          <Text variant="b12" color="placeholder">
            Prod API Credentials
          </Text>
        </Space>
      </Space>
    </Conditional>
  );

  const renderPendingCardContent = () => (
    <div className="third-party-payment-method active">
      <Space justifyContent="space-between" alignItems="center">
        <Space className="payment-method-card-header">
          <CustomIcons componentName={PAYTM} />
          <Conditional if={!isEmpty(paytmTestConfig)}>
            <div className="status pending">{accountStatus}</div>
          </Conditional>
        </Space>
        <Conditional if={!isEmpty(paytmTestConfig)}>
          <Button
            secondary
            size="small"
            className="cta-wrapper"
            onClick={connectAccount}
          >
            {stage === COMPLETED ? 'Submit Credentials' : 'Complete Test Txn.'}
          </Button>
        </Conditional>
      </Space>
      <Space justifyContent="space-between" alignItems="center">
        <div className="description">{getDescriptionByStage()}</div>
        <Conditional if={isEmpty(paytmTestConfig)}>
          <Button
            secondary
            size="small"
            className="cta-wrapper"
            onClick={() => resetModalName(PAYTM_MODAL)}
            loading={loading}
            disabled={loading}
          >
            Connect Account
          </Button>
        </Conditional>
      </Space>
      {renderStage()}
    </div>
  );

  return (
    <div className="third-party-payment-method-wrapper">
      {accountStatus === 'ACTIVE'
        ? renderActiveCardContent()
        : renderPendingCardContent()}

      <Conditional if={modalName === PAYTM_MODAL}>
        <PaytmModal
          data={{ paytmTestConfig, paytmProdConfig }}
          getPaytmCheckSumHash={getPaytmCheckSumHash}
          addPaytmTestConfig={addPaytmTestConfig}
          addPaytmProdConfig={addPaytmProdConfig}
          isEditMode={isEditMode}
          resetEditMode={resetEditMode}
          openModal={resetModalName}
          closeModal={closeModal}
          testLoader={testLoader}
          resetTestLoader={resetTestLoader}
          showPaytmForm={showPaytmForm}
          resetShowPaytmForm={resetShowPaytmForm}
          accountStatus={accountStatus}
        />
      </Conditional>
      <Conditional if={modalName === PAYTM_REPORT_GENERATED}>
        <AlertModal
          type="success"
          title="Test is successful"
          confirmText="Open Gmail"
          cancelText="Close"
          onConfirm={() => window.open(GMAIL_LINK, '_blank')}
          onClose={closeModal}
          eventNameOnClose="Test_Is_Successful_Close"
          eventNameOnConfirm="Open_Gmail_Confirm"
        >
          <Text variant="p14" color="bodyLight">
            The transaction report has been shared with you on{' '}
            <span className="email w-600">
              {getItemFromLocalStorage('email')}
            </span>
            , share it with your PayTM account manager.
            <br />
            <br />
            You will be required to submit the production credentials on
            Cashfree&apos;s dashboard once they are generated on your PayTM
            dashboard.
          </Text>
        </AlertModal>
      </Conditional>
      <Conditional if={modalName === CONNECT_PAYTM_ACCOUNT_SUCCESS}>
        <AlertModal
          type="success"
          title="Paytm Activated Successfully"
          onClose={closeModal}
          eventNameOnClose="Paytm_Activated_Successfully_Close"
        >
          <Text variant="p14" color="bodyLight">
            Your Paytm wallet has been successfully linked with Cashfree. It
            will now appear as a payment option on your checkout page.
          </Text>
        </AlertModal>
      </Conditional>
    </div>
  );
}

Paytm.propTypes = {
  data: PropTypes.object.isRequired,
  getPaytmTestConfig: PropTypes.func.isRequired,
  getPaymentMethods: PropTypes.func.isRequired,
  getPaytmCheckSumHash: PropTypes.func.isRequired,
  addPaytmProdConfig: PropTypes.func.isRequired,
  addPaytmTestConfig: PropTypes.func.isRequired,
  // openConfirmModal: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  showToast: PropTypes.func.isRequired,
  checkPaymentMethodEnabled: PropTypes.func.isRequired,
};

export default memo(Paytm);
